import { Injectable } from '@angular/core';
import { BaseData, Dataset } from '@models/mood/dataset';
import { ParentVectorDataset } from '@models/mood/vector';
import { ApplicationCoreModule } from '../core.module';
import { GroupedMetadata } from '@models/mood/metadata';
import { COVARIATE_DATASET_TYPE_ENUM } from '@models/mood/covariates/dataset';
import { Category } from '@models/mood/covariates/category';
import { GroupedDatasetNode, GroupedTreeNode, GroupsTreeNode, IDatasetFileTreeNode, IDatasetTreeNode, IModelTreeNode, ITreeNode, TreeNodeType } from './tree.interface';
import { ExternalReferenceType_ENUM } from '@models/mood/dataset-metainfo';
import { IGroup } from '@models/general/group';

const FOURIER_LABEL_ORDER = ['minimum', 'maximum', 'mean', 'amplitude', 'phase', 'variance'];

export const UNCATEGORIZED: GroupedTreeNode = {
  label: "Uncategorized",
  type: TreeNodeType.GROUP,
  category_id: 'no_category',
  node_order: 9999999, // All the way at the end
  children: [],
  sort_by_name: true
}

@Injectable({
  providedIn: ApplicationCoreModule
})
export class TreeGeneratorService {
  public constructTreeFromCategories(sets: BaseData[], categories: Category[], expand?: boolean) {
    let treeNodes: ITreeNode[] = this.createCategoryTree(categories);

    treeNodes.push({
      ...UNCATEGORIZED,
      children: [] // Force children empty, when datasets get updated, the old datasets are sticky
    } as GroupedTreeNode);

    sets.forEach((set, index) => {
      const categoryNode = this._findCategoryNode(treeNodes as GroupedTreeNode[], set.category_id || UNCATEGORIZED.category_id)!;

      let node: Partial<IDatasetTreeNode> = {
        label: set.name,
        type: TreeNodeType.DATASET,
        dataset_id: set.id,
        node_order: set.group_order,
        original_dataset_type: set.type,
      }

      if (set.type !== COVARIATE_DATASET_TYPE_ENUM.VECTOR) {
        node.metainfo_id = (set as Dataset).external_references?.find(reference => reference.platform === ExternalReferenceType_ENUM.GEONETWORK)?.identifier;
        node.unvalidated = !(set as Dataset).validated;
        node.tooltip = (set as Dataset).description;
      }

      if (set.type === COVARIATE_DATASET_TYPE_ENUM.SINGLE || set.type === COVARIATE_DATASET_TYPE_ENUM.TIMESERIES) {
        node.data_type = "SINGLE";
        node.file_type = "RASTER";
      }

      if (expand && set.type === COVARIATE_DATASET_TYPE_ENUM.VECTOR) {
        let groupedNode = node as GroupedDatasetNode;

        node.file_type = "VECTOR";

        groupedNode.data_type = TreeNodeType.GROUP;
        groupedNode.children = [];

        const vectorSet = set as ParentVectorDataset;

        if (vectorSet.children.length > 1) {
          vectorSet.children.forEach(file => {
            groupedNode.children!.push({
              type: TreeNodeType.DATASET,
              data_type: "GROUP",
              file_type: "VECTOR",
              filekey: "",
              label: file.name,
              dataset_id: file.id
            });
          });
        }
        else {
          node.type = TreeNodeType.DATASET; // 'DATASET';
          (node as IDatasetFileTreeNode).filekey = "";
          node.label = vectorSet.children[0].name;
          node.dataset_id = vectorSet.children[0].id
        }
      } else if (expand && (set.type === COVARIATE_DATASET_TYPE_ENUM.GROUPED || set.type === COVARIATE_DATASET_TYPE_ENUM.FOURIER)) {
        let groupedNode = node as GroupedDatasetNode;

        const rasterSet = set as Dataset;
        // groupedNode.metainfo_id = rasterSet.metainfo_id;
        groupedNode.data_type = TreeNodeType.GROUP;

        let meta = rasterSet.metadata as GroupedMetadata;

        groupedNode.children = [];

        meta.files.forEach(file => {
          groupedNode.children!.push({
            label: file.description,
            type: TreeNodeType.DATASET,
            data_type: "GROUP",
            file_type: "RASTER",
            dataset_id: rasterSet.id,
            filekey: file.filename
          });
        });

        if (set.type === COVARIATE_DATASET_TYPE_ENUM.FOURIER) {
          groupedNode.children.sort((nodeA, nodeB) => {
            const nodeAOrder = FOURIER_LABEL_ORDER.findIndex(label => nodeA.label.toLowerCase().startsWith(label));
            const nodeBOrder = FOURIER_LABEL_ORDER.findIndex(label => nodeB.label.toLowerCase().startsWith(label));

            if (nodeAOrder === nodeBOrder) {
              return nodeA.label.localeCompare(nodeB.label);
            }
            return nodeAOrder - nodeBOrder;
          });
        }
      } else if (set.type === COVARIATE_DATASET_TYPE_ENUM.TIMESERIES) {
        let groupedNode = node as GroupedDatasetNode;
        groupedNode.children = [];
      }
      categoryNode.children!.push(node as IDatasetTreeNode);
      categoryNode.children!.sort((a, b) => (a.node_order ?? 0) - (b.node_order ?? 0));

      sets[index].categories = categoryNode.categories || [];
    });


    treeNodes = this._filterEmptyTreeNode(treeNodes);

    return treeNodes;
  }


  private _findCategoryNode(arrayToWorkWith: GroupedTreeNode[], category_id: string) {
    const existingCategoryLeaf = this._findById(arrayToWorkWith, category_id);

    if (!existingCategoryLeaf) {
      return this._findById(arrayToWorkWith, UNCATEGORIZED.category_id);
    }

    return existingCategoryLeaf!;
  }

  private _findById(array: GroupedTreeNode[], category_id: string): GroupedTreeNode | null {
    return array.reduce<GroupedTreeNode | null>((a, item) => {
      if (a) return a;
      if (item.category_id === category_id) return item;
      if (item.children) return this._findById(item.children as GroupedTreeNode[], category_id);
      return null;
    }, null);
  }

  public createCategoryTree(nodes: Category[], category_id?: string, parent_categories?: string[]) {
    const childNodes = nodes.filter(node => node.parent_id == category_id);

    const treeNodes: GroupedTreeNode[] = childNodes.map(childNode => {
      const nodeCategories = [...parent_categories || [], childNode.label];
      return {
        sort_by_name: childNode.sort_by_name,
        label: childNode.label,
        categories: nodeCategories,
        type: TreeNodeType.GROUP,
        node_order: childNode.order,
        category_id: childNode.id,
        children: this.createCategoryTree(nodes, childNode.id, nodeCategories)
      } as GroupedTreeNode;
    });

    return treeNodes;
  }

  public createGroupTree(nodes: IGroup[]) {
    const childNodes = nodes;

    const treeNodes: GroupsTreeNode[] = childNodes.map(childNode => {
      const nodeCategories = [childNode.name];
      return {
        label: childNode.name,
        groups: nodeCategories,
        type: TreeNodeType.GROUP,
        group_id: childNode.id,
      } as GroupsTreeNode;
    });

    return treeNodes;
  }


  // public constructNewTreeNodes(sets: BaseData[], expand?: boolean) {
  //   let results: ITreeNode[] = JSON.parse(JSON.stringify(STATIC_TREE_STRUCTURE));


  //   sets.forEach((set, index) => {
  //     let tempNode = results;

  //     set.grouping.forEach(group => {
  //       tempNode = this._findOrGenerateLeaf(tempNode as GroupedTreeNode[], group).children!;
  //     });

  //     let node: Partial<IDatasetTreeNode> = {
  //       label: set.name,
  //       type: TreeNodeType.DATASET,
  //       dataset_id: set.dataset_id,
  //       node_order: set.group_order,
  //       original_dataset_type: set.type,
  //     }

  //     if (set.type !== COVARIATE_DATASET_TYPE.VECTOR) {
  //       node.metainfo_id = (set as Dataset).metainfo_id;
  //       node.unvalidated = !(set as Dataset).validated;
  //       node.tooltip = (set as Dataset).description;
  //     }

  //     if (set.type === COVARIATE_DATASET_TYPE.SINGLE || set.type === COVARIATE_DATASET_TYPE.TIMESERIES) {
  //       node.data_type = "SINGLE";
  //       node.file_type = "RASTER";
  //     }

  //     if (expand && set.type === COVARIATE_DATASET_TYPE.VECTOR) {
  //       let groupedNode = node as GroupedDatasetNode;

  //       node.file_type = "VECTOR";

  //       groupedNode.data_type = TreeNodeType.GROUP;
  //       groupedNode.children = [];

  //       const vectorSet = set as ParentVectorDataset;

  //       if (vectorSet.children.length > 1) {
  //         vectorSet.children.forEach(file => {
  //           groupedNode.children!.push({
  //             type: TreeNodeType.DATASET,
  //             data_type: "GROUP",
  //             file_type: "VECTOR",
  //             filekey: "",
  //             label: file.name,
  //             dataset_id: file.dataset_id
  //           });
  //         });
  //       } else {
  //         node.type = TreeNodeType.DATASET;
  //         (node as IDatasetFileTreeNode).filekey = "";
  //         node.label = vectorSet.children[0].name,
  //           node.dataset_id = vectorSet.children[0].dataset_id
  //       }
  //     } else if (expand && (set.type === COVARIATE_DATASET_TYPE.GROUPED || set.type === COVARIATE_DATASET_TYPE.FOURIER)) {
  //       let groupedNode = node as GroupedDatasetNode;

  //       const rasterSet = set as Dataset;
  //       // groupedNode.metainfo_id = rasterSet.metainfo_id;
  //       groupedNode.data_type = TreeNodeType.GROUP;

  //       let meta = rasterSet.metadata as GroupedMetadata;

  //       groupedNode.children = [];

  //       meta.files.forEach(file => {
  //         groupedNode.children!.push({
  //           label: file.description,
  //           type: TreeNodeType.DATASET,
  //           data_type: "GROUP",
  //           file_type: "RASTER",
  //           dataset_id: rasterSet.dataset_id,
  //           filekey: file.filename
  //         });
  //       });

  //       if (set.type === COVARIATE_DATASET_TYPE.FOURIER) {
  //         groupedNode.children.sort((nodeA, nodeB) => {
  //           const nodeAOrder = FOURIER_LABEL_ORDER.findIndex(label => nodeA.label.toLowerCase().startsWith(label));
  //           const nodeBOrder = FOURIER_LABEL_ORDER.findIndex(label => nodeB.label.toLowerCase().startsWith(label));

  //           if (nodeAOrder === nodeBOrder) {
  //             return nodeA.label.localeCompare(nodeB.label);
  //           }
  //           return nodeAOrder - nodeBOrder;
  //         });
  //       }
  //     } else if (set.type === COVARIATE_DATASET_TYPE.TIMESERIES) {
  //       let groupedNode = node as GroupedDatasetNode;
  //       groupedNode.children = [];
  //     }
  //     tempNode.push(node as IDatasetTreeNode);
  //     tempNode.sort((a, b) => (a.node_order ?? 0) - (b.node_order ?? 0))

  //   })

  //   results = this._filterEmptyTreeNode(results);

  //   return results;
  // }

  private _filterEmptyTreeNode(nodes: ITreeNode[] = []) {
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
      // if (nodes[i].type === "DATASET") {
      const node = nodes[i] as (GroupedDatasetNode | IModelTreeNode);
      const childNodes = this._filterEmptyTreeNode(node.children);

      if (node.children) {
        (nodes[i] as GroupedTreeNode).children = childNodes;
      }

      if (node.sort_by_name) {
        childNodes.sort((a, b) => {
          if (a.node_order || b.node_order) {
            return (a.node_order ?? 0) - (b.node_order ?? 0);
          }

          return a.label.localeCompare(b.label)
        });
      }

      if (!((node as GroupedDatasetNode).dataset_id || (node as IModelTreeNode).model_id) && childNodes.length == 0) {
        nodes.splice(i, 1);
      }
      // } else if(nodes[i].type === "MODEL") {

      // }
    }
    return nodes;
  }

  // private _findOrGenerateLeaf(arrayToWorkWith: GroupedTreeNode[], group_id: string) {
  //   let existingLeafIndex = arrayToWorkWith.findIndex(node => node.label == group_id);

  //   if (existingLeafIndex == -1) {
  //     const newLength = arrayToWorkWith.push({
  //       label: group_id,
  //       // uid: key,
  //       type: TreeNodeType.GROUP,
  //       category_id: '',

  //       children: []
  //     });

  //     existingLeafIndex = newLength - 1;
  //   }

  //   return arrayToWorkWith[existingLeafIndex];
  // }
}

// export const STATIC_TREE_STRUCTURE: GroupedTreeNode[] = [
//   {
//     label: "Temperature",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: [
//       {
//         label: "Land surface temperature (LST)",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: [
//           {
//             label: "MODIS LST Fourier",
//             type: TreeNodeType.GROUP,
//             category_id: '',
//             children: []
//           },
//           {
//             label: "MODIS LST time series",
//             type: TreeNodeType.GROUP,
//             category_id: '',
//             children: []
//           },
//           {
//             label: "ERA5 LST time series",
//             type: TreeNodeType.GROUP,
//             category_id: '',
//             children: []
//           }
//         ] as GroupedTreeNode[]
//       },
//       {
//         label: "Air temperature (AT)",
//         children: [
//           {
//             label: "ERA5 Air temperature 2m time series",
//             type: TreeNodeType.GROUP,
//             children: []
//           }
//         ],
//         type: TreeNodeType.GROUP
//       }
//     ] as GroupedTreeNode[]
//   },
//   {
//     label: "Vegetation index",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: [
//       {
//         label: "NDVI",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: [
//           {
//             label: "MODIS NDVI Fourier",
//             type: TreeNodeType.GROUP,
//             category_id: '',
//             children: []
//           },
//           {
//             label: "MODIS NDVI time series",
//             type: TreeNodeType.GROUP,
//             children: []
//           }
//         ] as GroupedTreeNode[]
//       },
//       {
//         label: "EVI",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: [
//           {
//             label: "MODIS EVI Fourier",
//             type: TreeNodeType.GROUP,
//             category_id: '',
//             children: []
//           },
//           {
//             label: "MODIS EVI time series",
//             type: TreeNodeType.GROUP,
//             children: []
//           }
//         ] as GroupedTreeNode[]
//       }
//     ] as GroupedTreeNode[]
//   },
//   {
//     label: "Precipitation",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: [
//       {
//         label: "Precipitation Fourier",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: []
//       },
//       {
//         label: "Precipitation time series",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: []
//       }
//     ] as GroupedTreeNode[]
//   },
//   {
//     label: "Relative Humidity (RH)",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: [
//       {
//         label: "Fourier",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: []
//       },
//       {
//         label: "Time series",
//         type: "GROUP",
//         children: []
//       }
//     ] as GroupedTreeNode[]
//   },
//   {
//     label: "Infrared (MODIS Channel 3)",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: []
//   },
//   {
//     label: "Daylight",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: []
//   },
//   {
//     label: "Elevation (DEM)",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: [
//       {
//         label: "Copernicus DEM",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: []
//       },
//       {
//         label: "USGS GMTED DEM",
//         type: TreeNodeType.GROUP,
//         children: []
//       }
//     ] as GroupedTreeNode[]
//   },
//   {
//     label: "Hydrography",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: []
//   },
//   {
//     label: "Land Cover / Land use",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: []
//   },
//   {
//     label: "Host and vector distributions",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: [
//       {
//         label: "Human population",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: []
//       },
//       {
//         label: "Wildlife hosts",
//         type: TreeNodeType.GROUP,
//         category_id: '',
//         children: [
//           {
//             label: "TBE Hosts",
//             type: TreeNodeType.GROUP,
//             category_id: '',
//             children: []
//           }
//         ] as GroupedTreeNode[]
//       },
//       {
//         label: "Vectors",
//         type: TreeNodeType.GROUP,
//         children: []
//       }
//     ] as GroupedTreeNode[]
//   },
//   {
//     label: "Infrastructure",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: []
//   },
//   {
//     label: "Administrative Layers (NUTS)",
//     type: TreeNodeType.GROUP,
//     category_id: '',
//     children: [],
//     sort_by_name: true
//   }
// ];
